import type { RevenueFilters } from './api';

export const revenueQueueUrl = (queue: 'pending' | 'denials', filters: RevenueFilters): string => {
  const params = new URLSearchParams({
    tab: 'claims',
    view: queue === 'pending' ? 'awaiting_payer' : 'fix',
    window: 'all',
  });
  if (queue === 'denials') params.set('status', 'denied');
  if (filters.providerId) params.set('provider', filters.providerId);
  if (filters.payerId) params.set('payer', filters.payerId);
  return `/jotbill?${params}`;
};

export const readRevenueFilters = (params: URLSearchParams): RevenueFilters => {
  const integer = (key: string, max: number): number | undefined => {
    const value = params.get(key);
    if (value === null || !/^\d+$/.test(value)) return undefined;
    const number = Number(value);
    return number <= max ? number : undefined;
  };
  const date = (key: string): string | undefined => {
    const value = params.get(key);
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().startsWith(value)
      ? value
      : undefined;
  };
  const outcome = params.get('outcome');
  return {
    dateFrom: date('from'),
    dateTo: date('to'),
    providerId: params.get('provider') || undefined,
    payerId: params.get('payer') || undefined,
    payerName: params.get('payerName') || undefined,
    outcome:
      outcome === 'clean' || outcome === 'denied' || outcome === 'other' || outcome === 'pending'
        ? outcome
        : undefined,
    lagBucket: integer('lag', 4),
    ageBucket: integer('age', 5),
    search: params.get('search') || undefined,
    patientSearch: params.get('patientSearch') || undefined,
    patientAgeBucket: integer('patientAge', 5),
    offset: integer('offset', 100000) ?? 0,
    patientOffset: integer('patientOffset', 100000) ?? 0,
    limit: 25,
  };
};

const queryKeys: Record<keyof Omit<RevenueFilters, 'entityId'>, string> = {
  dateFrom: 'from',
  dateTo: 'to',
  providerId: 'provider',
  payerId: 'payer',
  payerName: 'payerName',
  outcome: 'outcome',
  lagBucket: 'lag',
  ageBucket: 'age',
  search: 'search',
  offset: 'offset',
  patientOffset: 'patientOffset',
  patientSearch: 'patientSearch',
  patientAgeBucket: 'patientAge',
  limit: 'limit',
};
export function updateRevenueFilters(
  params: URLSearchParams,
  patch: Partial<RevenueFilters>,
): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('offset');
  next.delete('patientOffset');
  for (const key of Object.keys(patch) as (keyof RevenueFilters)[]) {
    if (key === 'entityId') continue;
    const value = patch[key];
    if (value === undefined || value === null || value === '') next.delete(queryKeys[key]);
    else next.set(queryKeys[key], String(value));
  }
  return next;
}
